/**
 * Parents' Home after setup (Jens 2026-10-15): a short "Get started" checklist and, after the
 * first week, an invitation to set up pocket money. Pure rules (tested); what a parent ticked off or
 * dismissed is remembered on the device (it is only a convenience).
 */
export type StartItem = 'approve' | 'own_task' | 'home_screen' | 'calendar';
export const START_ITEMS: StartItem[] = ['approve', 'own_task', 'home_screen', 'calendar'];

export type StartState = { done: StartItem[]; dismissed: boolean };

const DAY = 24 * 60 * 60 * 1000;

/** Which items are done: some the app can see by itself, the rest the parent ticked off. */
export function doneItems(
  state: StartState,
  family: { tasks?: Array<{ verified?: boolean }> },
  installed: boolean,
): Set<StartItem> {
  const done = new Set(state.done);
  if ((family.tasks || []).some((t) => t.verified)) done.add('approve');
  if (installed) done.add('home_screen');
  return done;
}

export function showChecklist(state: StartState, done: Set<StartItem>): boolean {
  return !state.dismissed && START_ITEMS.some((i) => !done.has(i));
}

/** The pocket money invitation: a family with children, a week after setup, not set up or declined. */
export function showPocketInvite(
  family: { pocket_money_enabled?: boolean; onboarding_completed_at?: Date | string | null; members?: Array<{ member_type?: string }> },
  dismissed: boolean,
  now: Date = new Date(),
): boolean {
  if (dismissed || family.pocket_money_enabled) return false;
  if (!(family.members || []).some((m) => m.member_type === 'child')) return false;
  const since = family.onboarding_completed_at ? new Date(family.onboarding_completed_at as any).getTime() : NaN;
  return Number.isFinite(since) && now.getTime() - since >= 7 * DAY;
}

// ── Remembered on this device ──────────────────────────────────────────────────────────────────
const key = (familyId: string, what: string) => `antnest:${what}:${familyId}`;

export function readStart(familyId: string): StartState {
  try {
    const raw = JSON.parse(localStorage.getItem(key(familyId, 'getting-started')) || 'null');
    return { done: Array.isArray(raw?.done) ? raw.done : [], dismissed: raw?.dismissed === true };
  } catch {
    return { done: [], dismissed: false };
  }
}
export function writeStart(familyId: string, state: StartState) {
  try { localStorage.setItem(key(familyId, 'getting-started'), JSON.stringify(state)); } catch { /* private mode */ }
}
export function readPocketDismissed(familyId: string): boolean {
  try { return localStorage.getItem(key(familyId, 'pocket-invite')) === 'no'; } catch { return false; }
}
export function writePocketDismissed(familyId: string) {
  try { localStorage.setItem(key(familyId, 'pocket-invite'), 'no'); } catch { /* private mode */ }
}

/** Running as an app from the home screen. */
export function isInstalled(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  } catch {
    return false;
  }
}
